// VenueEventList — chronological lists of upcoming + recent past events
// at one venue. Past list capped at 90 days back.
// /bolder pass: brand-color left-bar accent, weekday-prefixed dates in mono,
// magazine-style section headers with a leading color bar and trailing rule.
// /layout pass: month grouping headers, THIS WEEK orange callout band,
// sticky month strap on desktop scroll.

/* Venue entry-rules rail (ENTRY chip → RulesPopover) — the SAME shared
   module Events.jsx's card consumes (gate + matcher + chip tokens + the
   one-fetch-per-page hook), so the homepage card and this venue row can
   never disagree about whether a venue has rules. venue.html does NOT load
   Events.jsx, which is exactly why this logic lives in lib/ and not on
   window. */
import {
  useVenuePolicies,
  policyChipForEvent,
  rulesChipStyle,
  rulesChipSquareStyle,
  rulesChipSquareHidden,
  rulesChipAria,
  rulesChipTitle,
  rulesChipLabel,
  chipKeyDownGuard,
} from '../lib/venue-policies-gate.js';

/* Occasion kicker (seasonal lane C3, Ruling R13): the SAME module the
   homepage card's chip row reads, so this row's pill is the card's pill:
   one gate, one label, one set of tokens, no second copy of the styles. */
import { occasionKicker } from '../lib/occasion-kicker.js';

const VEL_AMBER = '#D57800';
// VEL_AMBER_TINT removed 2026-05-27: the upcoming row's hover tint is
// now produced by `.surface-wash--amber-upcoming` in colors_and_type.css
// (Zora side-stripe replacement spec).
const VEL_WARM_DARK = '#1A1A18';
const VEL_INTL_ORANGE = '#FF4F00';

// Scheme guard — mirrors Map.jsx / Events.jsx safeHref. Returns the URL only
// when it is a string starting with http:// or https://. Anything else
// (javascript:, data:, vbscript:, null) returns null so callers can suppress
// rendering of <a href> entirely. Venue.html does NOT load Events.jsx so we
// inline rather than rely on window.safeHref.
const _safeHref = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u)) ? u : null;

// Defensively unwrap provenance-wrapped fields. Once Sable's WRAPPABLE_FIELDS
// extension lands, event_url (and similar future per-event source URLs) will
// arrive either as a flat string (legacy / not-yet-wrapped scrapers) or as
// `{value, source, set_at}` (wrapped by worker/spine.js wrapScrapedEvent).
// Tolerate both shapes so this component doesn't need a coordinated deploy
// with the worker.
function _unwrapField(field) {
  if (field == null) return null;
  if (typeof field === 'object' && 'value' in field) return field.value;
  return field;
}

// Extract a clean host label from a URL — "https://live.stanford.edu/events/..."
// becomes "live.stanford.edu". Strips leading "www." for visual compactness.
// Returns null on parse failure so the caller can short-circuit rendering.
function _hostLabel(urlString) {
  try {
    const h = new URL(urlString).hostname;
    return h.replace(/^www\./i, '');
  } catch {
    return null;
  }
}

// Magazine section header — small leading bar + uppercase Be Vietnam + rule
// extending to the right edge. `currentColor` so it picks up theme color
// naturally for both dark and light modes.
function SectionHeader({ children, dim = false }) {
  return (
    <h2 style={{
      fontFamily: "'Be Vietnam Pro', sans-serif",
      fontWeight: 800,
      fontSize: 'clamp(18px, 4.6vw, 22px)',
      lineHeight: 1.1,
      textTransform: 'uppercase',
      letterSpacing: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      margin: '48px 0 24px',
      // #6B5B4F passes AA on light cream (6.5:1); CSS var resolves to a higher
      // contrast value (#A38D7C on dark — 6.3:1) per theme. Old fallback #aaa
      // failed AA at 2.9:1 on white.
      color: dim ? 'var(--on-surface-muted, #6B5B4F)' : 'inherit',
    }}>
      <span style={{
        width: 24,
        height: 2,
        background: 'currentColor',
        flexShrink: 0,
      }} />
      <span>{children}</span>
      <span style={{
        flex: 1,
        height: 1,
        background: 'currentColor',
        opacity: 0.2,
      }} />
    </h2>
  );
}

// Sub-rhythm month header — same em-dash-color-bar pattern as SectionHeader
// but smaller, thinner trailing rule, and (on desktop) sticks to viewport top
// as the user scrolls through that month's events.
// `isFirst` suppresses the extra 32px top gap before the first month group.
function MonthHeader({ label, isFirst, isDesktop, darkMode = true }) {
  // When sticky on desktop, paint with the GLASS panel variant so the bar
  // reads as floating chrome (inner glow + edge highlight). Non-sticky mobile
  // stays flat — no perf cost from blur during mobile scroll.
  const glass = isDesktop && typeof window !== 'undefined' && window.glassStyle
    ? window.glassStyle(darkMode, 'panel')
    : null;
  return (
    <h3 style={{
      fontFamily: "'Be Vietnam Pro', sans-serif",
      fontWeight: 700,
      fontSize: 14,
      lineHeight: 1.1,
      textTransform: 'uppercase',
      letterSpacing: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      margin: isFirst ? '0 0 12px' : '32px 0 12px',
      color: 'inherit',
      position: isDesktop ? 'sticky' : 'static',
      top: isDesktop ? 44 : 'auto',
      zIndex: 10,
      padding: '8px 12px',
      ...(glass || {
        background: 'color-mix(in oklch, var(--surface, #121414) 92%, transparent)',
      }),
    }}>
      <span style={{
        width: 16,
        height: 2,
        background: VEL_AMBER,
        flexShrink: 0,
      }} />
      <span>{label}</span>
      <span style={{
        flex: 1,
        height: 1,
        background: 'var(--fog-blue-rule, #7A99AC)',
        opacity: 0.3,
      }} />
    </h3>
  );
}

function ThisWeekBand({ count, darkMode = true }) {
  // Liquid glass layered as inner shadows on top of the orange poster
  // background — keeps the international-orange ink reading as poster
  // but adds the floating-chrome inner glow that signals "live now" beat.
  const glassShadow = darkMode
    ? 'inset 0px 0px 16px 0px rgba(255,242,230,0.18), inset 0px 0px 3px 0px rgba(255,255,255,0.22), inset -1px -1px 0.5px -1px rgba(255,255,255,0.35), inset 1px 1px 0.5px -1px rgba(255,255,255,0.35), 4px 4px 0 #1A1A18'
    : 'inset 0px 0px 16px 0px rgba(255,255,255,0.28), inset 0px 0px 3px 0px rgba(255,255,255,0.4), inset -1px -1px 0.5px -1px rgba(255,255,255,0.55), inset 1px 1px 0.5px -1px rgba(255,255,255,0.55), 4px 4px 0 #1A1A18';
  return (
    <div style={{
      background: VEL_INTL_ORANGE,
      color: 'var(--on-international-orange)',
      height: 40,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      boxShadow: glassShadow,
      borderRadius: 2,
      margin: '0 0 16px',
    }}>
      <span style={{
        fontFamily: "'Be Vietnam Pro', sans-serif",
        fontWeight: 800,
        fontSize: 12,
        lineHeight: 1,
        letterSpacing: 3,
        textTransform: 'uppercase',
      }}>This Week</span>
      <span style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontWeight: 700,
        fontSize: 12,
        lineHeight: 1,
        letterSpacing: 2,
        textTransform: 'uppercase',
        fontVariantNumeric: 'tabular-nums',
      }}>{count} {count === 1 ? 'Show' : 'Shows'}</span>
    </div>
  );
}

// Format an event's date with weekday prefix from iso_date.
// "May 26" + iso_date "2026-05-26" → "TUE MAY 26"
function formatRowDate(ev) {
  const iso = ev.iso_date;
  if (iso) {
    const d = new Date(iso + 'T00:00:00Z');
    if (!isNaN(d.getTime())) {
      const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
      const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase();
      const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' });
      return `${weekday} ${month} ${day}`;
    }
  }
  // Fall back to whatever string date the event came with — uppercased.
  return (ev.date || '').toUpperCase();
}

// Inline expanded detail panel. Rendered as a sibling <li> directly below
// the row when expandedId matches. Uses max-height transition for animation.
// Pulls fields directly from the event object — no extra fetch needed; all
// data is already present in the events array fed to VenueEventList.
function ExpandedDetailPanel({ ev, isMobile, open, darkMode = true }) {
  return (
    // Side-stripe replacement (Zora 2026-05-26): Device B (10% amber wash)
    // + 1px top hairline replace the 5px amber border-left. Tray-open is
    // signaled by surface tint; structural position carries the rest.
    <li
      aria-hidden={!open}
      className="surface-wash--amber-expanded"
      style={{
        listStyle: 'none',
        overflow: 'hidden',
        maxHeight: open ? 800 : 0,
        opacity: open ? 1 : 0,
        transition: typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'none'
          : 'max-height 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease',
        padding: 0,
        margin: 0,
        borderTop: '1px solid color-mix(in oklch, currentColor 10%, transparent)',
      }}
    >
      <div style={{
        padding: isMobile ? '16px 14px 20px 14px' : '20px 24px 24px 24px',
        display: 'grid',
        gap: isMobile ? 12 : 16,
      }}>
        {ev.description && (
          <p style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: 15,
            lineHeight: 1.55,
            margin: 0,
            color: 'var(--on-surface, inherit)',
          }}>{ev.description}</p>
        )}

        {/* Grid auto-fits 3-4 cells depending on whether SOURCE renders.
          * Frost emits event_url today; other venue adapters can light up
          * the cell without code changes once they emit the same field. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: isMobile ? 10 : 20,
        }}>
          <div>
            <div style={{
              fontFamily: "'Be Vietnam Pro', sans-serif",
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: 'var(--fog-blue-ink, #A3BDCC)',
              marginBottom: 4,
            }}>When</div>
            <div style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 13,
              letterSpacing: 1,
              fontVariantNumeric: 'tabular-nums',
            }}>{formatRowDate(ev)}{typeof ev.startDate === 'string' && ev.startDate.includes('T') ? ` · ${new Date(ev.startDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}</div>
          </div>

          {ev.category && (
            <div>
              <div style={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: 'var(--fog-blue-ink, #A3BDCC)',
                marginBottom: 4,
              }}>Category</div>
              <div style={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontSize: 13,
                fontWeight: 500,
                textTransform: 'capitalize',
              }}>{ev.category}</div>
            </div>
          )}

          {(ev.interest_count != null) && (
            <div>
              <div style={{
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: 'var(--fog-blue-ink, #A3BDCC)',
                marginBottom: 4,
              }}>Pulling up</div>
              <div style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
                color: ev.interest_count > 0 ? (darkMode ? VEL_AMBER : 'var(--amber-gold-ink)') : 'inherit',
              }}>{ev.interest_count > 0 ? `${ev.interest_count} interested` : 'No signal yet'}</div>
            </div>
          )}

          {/* Per-event source URL. Surfaces when an adapter emits `event_url`
            * (e.g. Frost's Stanford Live URL) — universal so future venues
            * whose adapters carry source URLs light up the cell without
            * code changes. Unwraps both flat-string and provenance-wrapped
            * shapes (Sable's WRAPPABLE_FIELDS extension may wrap event_url
            * inside `{value, source, set_at}`). */}
          {(() => {
            const eventUrl = _safeHref(_unwrapField(ev.event_url));
            if (!eventUrl) return null;
            const host = _hostLabel(eventUrl);
            if (!host) return null;
            return (
              <div>
                <div style={{
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: 'var(--fog-blue-ink, #A3BDCC)',
                  marginBottom: 4,
                }}>Source</div>
                {/* 44px hit area enforced via min-height — the inline-flex box
                  * grows the tap target without changing the visible link
                  * text size. Stops bubbling so a tap on the link doesn't
                  * also trigger the row's expand toggle. */}
                <a
                  href={eventUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    fontSize: 13,
                    lineHeight: 1.3,
                    letterSpacing: 0,
                    color: 'var(--fog-blue-ink, #A3BDCC)',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                    display: 'inline-flex',
                    alignItems: 'center',
                    minHeight: 44,
                    // Negative top/bottom margin neutralizes the 44px box
                    // visually so the cell rhythm matches the others
                    // (Source label sits flush like When / Category).
                    margin: '-12px 0',
                    wordBreak: 'break-all',
                  }}
                >View on {host} →</a>
              </div>
            );
          })()}
        </div>

        {(() => {
          // Expanded-row CTA: branches on saleStatus same as the collapsed
          // row TICKETS cell — sold-out / canceled show a non-link pill so
          // we don't promise tickets we can't deliver.
          const offers = _unwrapField(ev.offers);
          const ticketHref = _safeHref(offers?.url);
          const saleStatus = _unwrapField(ev.saleStatus);
          if (saleStatus === 'sold-out' || saleStatus === 'canceled') {
            return (
              <span
                aria-label={saleStatus === 'sold-out' ? 'Sold out' : 'Canceled'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: isMobile ? 'stretch' : 'flex-start',
                  minHeight: 44,
                  padding: '0 20px',
                  background: 'transparent',
                  color: 'var(--on-surface-muted, #A38D7C)',
                  border: '1.5px solid color-mix(in oklch, var(--on-surface-muted, #A38D7C) 50%, transparent)',
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 2,
                  lineHeight: 1,
                  textTransform: 'uppercase',
                  borderRadius: 2,
                  marginTop: 4,
                }}
              >{saleStatus === 'sold-out' ? 'Sold out' : 'Canceled'}</span>
            );
          }
          if (!ticketHref) return null;
          const label = saleStatus === 'free' ? 'Free entry →' : 'Get tickets →';
          return (
            <a
              href={ticketHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof markInterested === 'function') markInterested(ev.id);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: isMobile ? 'stretch' : 'flex-start',
                minHeight: 44,
                padding: '0 20px',
                background: 'var(--eucalyptus-green, #395542)',
                color: '#F8F6F2',
                fontFamily: "'Be Vietnam Pro', sans-serif",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: 2,
                lineHeight: 1,
                textTransform: 'uppercase',
                textDecoration: 'none',
                boxShadow: `3px 3px 0 ${VEL_INTL_ORANGE}`,
                borderRadius: 2,
                marginTop: 4,
              }}
            >{label}</a>
          );
        })()}
      </div>
    </li>
  );
}

// Single upcoming row. Hover state (left-bar widen + warm tint background)
// is managed via React state — keeps it inline-style-pure, no global CSS.
// `hot` flag (passed for THIS WEEK rows) thickens the left bar and prepends
// a small THIS WEEK micro-tag to the date column.
// `isMobile` swaps the 3-col desktop grid for a stacked 2-row mobile layout:
//   row 1: [● THIS WEEK | TUE MAY 26] ............ [TICKETS →]
//   row 2: full-width title
// This keeps the date + tickets paired (the contextually-light tier) and gives
// the title the full mobile column width.
function UpcomingRow({ ev, hot = false, isMobile = false, expanded = false, onToggleExpand, darkMode = true }) {
  // `hover` is still tracked because the chevron focus ring + a few
  // downstream cells read it; the row's hover-tint is now handled by
  // the `.surface-wash--amber-upcoming` utility (Zora 2026-05-26).
  const [hover, setHover] = React.useState(false);

  // MerchPopover trigger state — holds the chip's bounding rect at click
  // time so the popover can FLIP-morph out of the chip. Mirrors the
  // homepage card pattern (Events.jsx). null = closed.
  const [merchTriggerRect, setMerchTriggerRect] = React.useState(null);
  // RulesPopover trigger state — same FLIP-rect pattern.
  const [rulesTriggerRect, setRulesTriggerRect] = React.useState(null);
  // Venue policies — shared one-fetch-per-page lookup; rows on a venue page
  // all resolve the same venue so this is one dictionary hit per row render.
  // ev.venue on scraper/user rows; venueName is the older VENUE_EVENTS field
  // — the gate matches on event.venue, so a venueName-only row is handed
  // over with the resolved label in that slot.
  const venuePolicies = useVenuePolicies();
  const rulesVenueLabel = ev.venue || ev.venueName || '';
  const rulesEvent = ev.venue ? ev : { ...ev, venue: rulesVenueLabel };
  const rulesPolicy = policyChipForEvent(venuePolicies, rulesEvent);

  const dateCell = (
    <span style={{
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      color: 'var(--amber-gold-ink)',
      fontSize: 13,
      lineHeight: 1.4,
      // 1px snaps to body-uppercase tracking (was 0.5 orphan). Mono numerals
      // already have generous side-bearings so 1 reads cleanly.
      letterSpacing: 1,
      fontWeight: 500,
      fontVariantNumeric: 'tabular-nums',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
      {hot && (
        // Side-stripe replacement (Zora 2026-05-26): Device A 8×2 tick bar
        // carries the "row is live" signal that the animated border-left
        // used to. `tick-bar--amber` ::before provides the stamp.
        <span
          className="tick-bar--amber"
          style={{
            color: 'var(--international-orange-ink)',
            fontFamily: "'Be Vietnam Pro', sans-serif",
            fontSize: 9,
            fontWeight: 800,
            // Tiny micro-label — bump tracking to 2 to match label scale.
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >This Week</span>
      )}
      <span>{formatRowDate(ev)}</span>
    </span>
  );

  // /e/ preview pages are keyed by generateSlug(title) (see scrapers/normalize.js).
  // VENUE_EVENTS rows carry event_hash but NOT the title-derived slug, so we
  // compute it client-side via window.eventSlug (lib/event-slug.js — kept in
  // byte-sync with the server function). userEvent overlay rows already carry
  // ev.slug so that wins when present.
  const previewSlug = ev.slug || (typeof window !== 'undefined' && window.eventSlug ? window.eventSlug(ev.title) : '') || ev.id || ev.event_hash;
  const titleCell = (
    <a href={`/e/${previewSlug}`} style={{
      color: 'inherit',
      textDecoration: 'none',
      fontFamily: "'Be Vietnam Pro', sans-serif",
      fontWeight: 700,
      // Keep 18px on mobile for tap target friendliness, breathe to 19px desktop.
      fontSize: 'clamp(18px, 2.2vw, 19px)',
      lineHeight: 1.25,
      // Mobile: explicit display:block so the link spans full row width
      // and the tap target stretches across the title line.
      display: isMobile ? 'block' : 'inline',
    }}>{ev.title}</a>
  );

  // ENTRY chip — venue rules + bag policy, FIRST in the chip cluster
  // (immediately LEFT of MERCH; info-before-commerce — same adjacency as
  // the homepage card). Gate + tokens from lib/venue-policies-gate.js; no
  // entry / suppressed → null (row byte-identical to before this feature).
  // stopPropagation + onKeyDown guards mirror the MERCH chip so the tap
  // never triggers the row's expand/slide behaviors and keyboard Enter
  // never bubbles into row handlers. Fallback when window.RulesPopover
  // hasn't loaded: open the venue's own policy page (spec §1).
  const rulesChip = rulesPolicy ? (
    <button
      type="button"
      className="focus-double-ring"
      onClick={(e) => {
        e.stopPropagation();
        if (typeof window.RulesPopover === 'function') {
          setRulesTriggerRect(e.currentTarget.getBoundingClientRect());
        } else {
          window.open(rulesPolicy.entry.source_url, '_blank', 'noopener,noreferrer');
        }
      }}
      onKeyDown={chipKeyDownGuard}
      aria-label={rulesChipAria(rulesChipLabel(rulesPolicy, rulesEvent), rulesPolicy.entry.source_kind)}
      aria-haspopup="dialog"
      title={rulesChipTitle(rulesChipLabel(rulesPolicy, rulesEvent), rulesPolicy.entry.source_kind)}
      style={rulesChipStyle(darkMode)}
    >
      {!rulesChipSquareHidden() && (
        <span aria-hidden="true" style={rulesChipSquareStyle(darkMode, rulesPolicy.state)} />
      )}
      ENTRY
    </button>
  ) : null;

  // MERCH chip — renders when the VENUE_EVENTS record carries a merch
  // attachment (written at scrape time by venue-spine.js / the TM bridges,
  // same attach-merch matcher as events.json rows). Follows the homepage
  // card pattern at Events.jsx EXACTLY: same visual chip, opens the
  // in-page MerchPopover (FLIP morph, z-index 2000) via window.MerchPopover,
  // falls back to the standalone /merch-preview.html popup when the popover
  // component hasn't loaded (race on first paint / no-JS). stopPropagation
  // so the tap never triggers the row's expand/slide behaviors.
  // aria-haspopup="dialog" + .focus-double-ring: 2026-08-20 parity retrofit
  // with the ENTRY chip above — the debt the rules spec §1 logged.
  const merchChip = ev.merch && typeof ev.merch.shop_url === 'string' && previewSlug ? (
    <button
      type="button"
      className="focus-double-ring"
      onClick={(e) => {
        e.stopPropagation();
        if (typeof window.MerchPopover === 'function') {
          setMerchTriggerRect(e.currentTarget.getBoundingClientRect());
        } else {
          const w = 760, h = 760;
          const left = Math.max(0, (window.screen.availWidth - w) / 2);
          const top = Math.max(0, (window.screen.availHeight - h) / 2);
          const url = `/merch-preview.html?slug=${encodeURIComponent(previewSlug)}`;
          window.open(url, `OnSumnMerch_${previewSlug}`,
            `popup,width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`);
        }
      }}
      aria-label={`Preview ${ev.merch.artist || 'artist'} merch`}
      aria-haspopup="dialog"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        // The row's amber wash sits under this chip (10% on hover), so it needs
        // more than the homepage card: light takes the MerchPopover ink (5.02:1,
        // #8E4E00 is 4.40:1 here) and dark a 0.08 fill (4.59:1 vs 4.33:1).
        color: darkMode ? '#D57800' : 'oklch(0.46 0.13 60)',
        background: darkMode ? 'rgba(213,120,0,0.08)' : 'rgba(213,120,0,0.18)',
        border: '1px solid rgba(213,120,0,0.55)',
        padding: '3px 8px', borderRadius: 100,
        lineHeight: 1.2, minHeight: 24, cursor: 'pointer',
      }}
      title="Click to view merch"
    >MERCH <span aria-hidden="true">→</span></button>
  ) : null;

  // Sliding chip + share icon — venue rows surface social proof and the
  // share affordance to the right of the title. We pass through ev.id as
  // the slug since VENUE_EVENTS rows use event_hash there and userEvent
  // overlay rows use slug||id (uniform via userEventToVenueShape).
  // SlidingChip auto-collapses below threshold, so quiet rows stay quiet.
  //
  // OCCASION kicker (Ruling R13): this row never renders EventCard, so the
  // card's kicker is carried here explicitly, first in the cluster and
  // immediately LEFT of ENTRY (DOM order = keyboard order: kicker → ENTRY →
  // MERCH → share). Same <a>, tokens and stopPropagation guards as the card
  // (lib/occasion-kicker.js); null for an untagged or unrostered row, which
  // keeps the cluster byte-identical to before. A tagged cluster may wrap
  // (row gap 6, chip gap still 8) so the longest label plus ENTRY, MERCH
  // and share never overflow a phone row.
  const occasionPill = occasionKicker(ev, darkMode);
  const slidingShareCell = ev.id && (
    <div style={occasionPill
      ? { display: 'inline-flex', alignItems: 'center', columnGap: 8, rowGap: 6, flexWrap: 'wrap' }
      : { display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {occasionPill && <a {...occasionPill.props}>{occasionPill.label}</a>}
      {rulesChip}
      {merchChip}
      {typeof SlidingChip === 'function' && (
        <SlidingChip slug={ev.id} darkMode={darkMode} />
      )}
      {typeof ShareIconButton === 'function' && (
        <ShareIconButton
          event={{
            title: ev.title,
            slug: ev.id,
            venue: ev.venueName,
            date: ev.startDate || ev.date,
          }}
          darkMode={darkMode}
        />
      )}
      {merchTriggerRect && typeof window.MerchPopover === 'function' && (
        <window.MerchPopover
          event={ev}
          darkMode={darkMode}
          triggerRect={merchTriggerRect}
          onClose={() => setMerchTriggerRect(null)}
        />
      )}
      {rulesTriggerRect && rulesPolicy && typeof window.RulesPopover === 'function' && (
        <window.RulesPopover
          event={ev}
          entry={rulesPolicy.entry}
          state={rulesPolicy.state}
          eventRules={rulesPolicy.eventRules}
          darkMode={darkMode}
          triggerRect={rulesTriggerRect}
          onClose={() => setRulesTriggerRect(null)}
        />
      )}
    </div>
  );

  // Tickets cell: branches on saleStatus captured by the scraper.
  // - sold-out / canceled → non-link "SOLD OUT" / "CANCELED" pill (honesty wins
  //   over a dead link to a vendor page that 404s on purchase)
  // - free → "FREE →" link (Open Mic etc. — still useful to surface)
  // - on-sale / null → "TICKETS →" link (default behavior)
  // WCAG AA: 44x44 touch on mobile preserved across all branches.
  // Unwrap provenance-wrapped offers (Frost adapter emits envelope shape).
  const ticketsHref = _safeHref(_unwrapField(ev.offers)?.url);
  const saleStatus = _unwrapField(ev.saleStatus);
  const ticketsBaseStyle = {
    fontFamily: "'Be Vietnam Pro', sans-serif",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: 2,
    textTransform: 'uppercase',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: isMobile ? 44 : 'auto',
    minWidth: isMobile ? 44 : 'auto',
    padding: isMobile ? '0 14px' : '12px 8px',
    margin: isMobile ? '-6px -10px -6px 0' : '-12px -8px -12px 0',
    alignSelf: 'center',
  };
  const ticketsCell = (() => {
    if (saleStatus === 'sold-out' || saleStatus === 'canceled') {
      return (
        <span
          style={{
            ...ticketsBaseStyle,
            color: 'var(--on-surface-muted, #A38D7C)',
            border: '1px solid color-mix(in oklch, var(--on-surface-muted, #A38D7C) 40%, transparent)',
            borderRadius: 2,
            padding: isMobile ? '0 12px' : '6px 10px',
            cursor: 'not-allowed',
          }}
          aria-label={saleStatus === 'sold-out' ? 'Sold out' : 'Canceled'}
        >{saleStatus === 'sold-out' ? 'SOLD OUT' : 'CANCELED'}</span>
      );
    }
    if (!ticketsHref) return null;
    const label = saleStatus === 'free' ? 'FREE →' : 'TICKETS →';
    return (
      <a
        href={ticketsHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          if (typeof markInterested === 'function') markInterested(ev.id);
        }}
        style={{ ...ticketsBaseStyle, color: 'var(--eucalyptus-green-ink)' }}
      >{label}</a>
    );
  })();

  const intelCell = ev.source_attribution && (
    <span style={{
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: 2,
      textTransform: 'uppercase',
      color: 'var(--eucalyptus-green-ink)',
      padding: '2px 6px',
      border: '1px solid color-mix(in oklch, var(--eucalyptus-green-light, #5A7A68) 40%, transparent)',
      borderRadius: 2,
      whiteSpace: 'nowrap',
      display: 'inline-block',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>INTEL: {ev.source_attribution}</span>
  );

  // Chevron is the EXPAND affordance — separate hit target from the card body
  // (which carries the sliding ritual) and the tickets link. 44x44 hit area.
  const [chevFocus, setChevFocus] = React.useState(false);
  const chevronCell = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        // Sliding ritual: tap = interested. The expand gesture IS the slide on
        // venue pages — mental model is "I'm interested → show me more". Mark
        // fires BEFORE expand so a fast unmount/navigate can't race the POST.
        // ev.id is uniform across scraper rows (= event_hash) and user-event
        // overlay rows (= slug||id via userEventToVenueShape).
        if (typeof markInterested === 'function') markInterested(ev.id);
        if (onToggleExpand) onToggleExpand();
      }}
      onFocus={() => setChevFocus(true)}
      onBlur={() => setChevFocus(false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          // Keyboard activation parity: Enter/Space must also fire interest.
          if (typeof markInterested === 'function') markInterested(ev.id);
          if (onToggleExpand) onToggleExpand();
        }
      }}
      aria-expanded={expanded}
      aria-label={expanded ? `Collapse details for ${ev.title}` : `Expand details for ${ev.title}`}
      style={{
        background: 'transparent',
        border: `2px solid ${chevFocus ? VEL_AMBER : 'transparent'}`,
        borderRadius: 2,
        outline: 'none',
        cursor: 'pointer',
        width: 44,
        height: 44,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--on-surface-muted, #6B5B4F)',
        padding: 0,
        margin: '-12px -8px -12px 0',
        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );

  if (isMobile) {
    return (
      <>
      {/* Side-stripe replacement (Zora 2026-05-26): Device B surface wash
          replaces the animated amber border-left. Hot rows additionally
          get the Device A 8×2 tick bar via the kicker — added inside the
          date cell. Hover-tint shift is under 200ms so reduced-motion
          is honored natively. */}
      <li
        className="surface-wash--amber-upcoming"
        style={{
          // Slightly more vertical breathing on mobile for thumb-friendly rows.
          padding: '14px 12px 14px 14px',
          borderTop: '1px solid color-mix(in oklch, currentColor 8%, transparent)',
          display: 'grid',
          // Two-row stack: row 1 = date | tickets | chev; row 2 = title full-width.
          gridTemplateColumns: '1fr auto auto',
          gridTemplateAreas: '"date tix chev" "title title title"',
          columnGap: 12,
          rowGap: 8,
          alignItems: 'center',
        }}
      >
        <div style={{ gridArea: 'date', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          {dateCell}
          {intelCell}
        </div>
        <div style={{ gridArea: 'tix', justifySelf: 'end' }}>{ticketsCell}</div>
        <div style={{ gridArea: 'chev', justifySelf: 'end' }}>{chevronCell}</div>
        <div style={{ gridArea: 'title' }}>
          {titleCell}
          {slidingShareCell && (
            <div style={{ marginTop: 6 }}>{slidingShareCell}</div>
          )}
        </div>
      </li>
      <ExpandedDetailPanel ev={ev} isMobile={true} open={expanded} darkMode={darkMode} />
      </>
    );
  }

  return (
    <>
    {/* Side-stripe replacement (Zora 2026-05-26): Device B surface wash
        carries upcoming-state; hover lift (6→10% tint) happens in the
        utility class. JS `hover` state retained only because some
        downstream cells (chevron focus ring) still read it. */}
    <li
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="surface-wash--amber-upcoming"
      style={{
        padding: '16px 16px 16px 16px',
        borderTop: '1px solid color-mix(in oklch, currentColor 8%, transparent)',
        display: 'grid',
        gridTemplateColumns: '120px 1fr auto auto',
        gap: 16,
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
        {dateCell}
        {intelCell}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
        {titleCell}
        {slidingShareCell}
      </div>
      {ticketsCell}
      {chevronCell}
    </li>
    <ExpandedDetailPanel ev={ev} isMobile={false} open={expanded} darkMode={darkMode} />
    </>
  );
}

// Past row — dimmed, thin amber bar at low opacity, weekday prefix too.
// Past titles drop to 15px (vs 19px upcoming) to reinforce visual hierarchy.
// On mobile we stack date above title (same pattern as UpcomingRow but no
// tickets cell so it's a simple 2-row stack).
function PastRow({ ev, isMobile = false }) {
  // Side-stripe replacement (Zora 2026-05-26): Device D — typography-only.
  // Past state is already conveyed by 15px title + muted color + no kicker;
  // the 2px tinted amber stripe was the weakest signal in the file. Removed.
  return (
    <li style={{
      padding: isMobile ? '10px 0 10px 12px' : '10px 0 10px 14px',
      borderTop: '1px solid color-mix(in oklch, currentColor 5%, transparent)',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '120px 1fr',
      gap: isMobile ? 4 : 16,
      alignItems: 'baseline',
    }}>
      <span style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        // #6B5B4F passes AA on light (6.5:1); CSS var resolves to #A38D7C on
        // dark (6.3:1). Replaces failing #888 fallback (3.5:1 on white).
        color: 'var(--on-surface-muted, #6B5B4F)',
        fontSize: 12,
        lineHeight: 1.4,
        // 1px joins body-uppercase tracking (was 0.5 orphan).
        letterSpacing: 1,
        fontVariantNumeric: 'tabular-nums',
      }}>{formatRowDate(ev)}</span>
      <span style={{
        fontFamily: "'Be Vietnam Pro', sans-serif",
        fontWeight: 500,
        fontSize: 15,
        lineHeight: 1.3,
      }}>{ev.title}</span>
    </li>
  );
}

// Return YYYY-MM-DD for an event (UTC), or '' if it has no parseable date.
function eventIsoDay(ev) {
  return ev.iso_date || (ev.startDate ? ev.startDate.slice(0, 10) : '');
}

// True if an event's date falls within the next 7 days (inclusive of today).
// Comparison is done in UTC midnight space to match the iso_date convention.
function isInNext7Days(ev, now = new Date()) {
  const day = eventIsoDay(ev);
  if (!day) return false;
  const eventDate = new Date(day + 'T00:00:00Z');
  if (isNaN(eventDate.getTime())) return false;
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const sevenDays = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  return eventDate.getTime() >= today.getTime() && eventDate.getTime() < sevenDays.getTime();
}

// Group events by YYYY-MM into ordered { label, events } buckets.
// `upcoming` is already chronological so insertion order is the right order.
function groupByMonth(events) {
  const groups = new Map();
  for (const ev of events) {
    const iso = eventIsoDay(ev);
    if (!iso) continue;
    const key = iso.slice(0, 7);
    const d = new Date(iso + 'T00:00:00Z');
    if (isNaN(d.getTime())) continue;
    const label = `${d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' })} ${d.getUTCFullYear()}`.toUpperCase();
    if (!groups.has(key)) groups.set(key, { key, label, events: [] });
    groups.get(key).events.push(ev);
  }
  return [...groups.values()];
}

function VenueEventList({ upcoming, past, darkMode = true, eventsGeneratedAt = null }) {
  // Desktop-only sticky month strap. matchMedia gate mirrors the VENUE 01 OF 5
  // label pattern in VenuePage so behavior is consistent across the page.
  // Architect ask #7: routed through the bp helper so the 640px boundary
  // stops drifting (was 639 in VenuePage, 640 here pre-fix). Kept at 640
  // (xs2 in lib/breakpoints.js) — bumping to md (768) would change
  // iPad-mini-landscape UX that the venue page is tuned around since
  // Phase 4.2. One resolver for both the initializer and the effect.
  const desktopMq = () => (window.bp && window.bp.minWidth)
    ? window.bp.minWidth('xs2')
    : window.matchMedia('(min-width: 640px)');
  // Seed from matchMedia rather than `false` (D19 follow-up, 2026-07-29) — see
  // the matching note in components/VenueHeader.jsx. `false` here means every
  // DESKTOP visitor paints one frame without the sticky month strap.
  const [isDesktop, setIsDesktop] = React.useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return desktopMq().matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = desktopMq();
    const update = () => setIsDesktop(mq.matches);
    update();
    // addEventListener is the supported path on modern Safari/Chrome/Firefox.
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);

  // `isMobile` is just the inverse of the existing `isDesktop` gate so we
  // don't introduce a second matchMedia source-of-truth. xs2 (640px) is
  // the shared boundary across the venue page: under it → stacked rows,
  // non-sticky month strap, looser tap targets.
  const isMobile = !isDesktop;

  // Batch-hydrate interest counts for visible upcoming rows. The 60s
  // localStorage TTL in fetchInterestCounts keeps revisits cheap.
  const upcomingSlugs = React.useMemo(
    () => (upcoming || []).map(e => e.id).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(upcoming || []).map(e => e.id).join('|')]
  );
  if (typeof useHydrateInterest === 'function') useHydrateInterest(upcomingSlugs);

  // Accordion state — only one row expanded at a time. null = nothing open.
  const [expandedId, setExpandedId] = React.useState(null);
  const toggleExpanded = React.useCallback((id) => {
    setExpandedId(prev => prev === id ? null : id);
  }, []);
  // Esc anywhere on the page collapses the open row.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setExpandedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Partition upcoming into "this week" vs "later" so each event renders
  // exactly once. Duplication between THIS WEEK band and month group reads
  // as a bug, so this-week events appear ONLY in the band.
  const thisWeek = upcoming.filter(ev => isInNext7Days(ev));
  const later = upcoming.filter(ev => !isInNext7Days(ev));
  const monthGroups = groupByMonth(later);
  const hasThisWeek = thisWeek.length > 0;

  return (
    <section style={{
      maxWidth: 1100,
      margin: '0 auto',
      // Tighter side padding on mobile to give rows more usable width — the
      // poster hero is full-bleed so a 16px gutter feels intentional here.
      padding: isMobile ? '0 16px 32px' : '0 24px 32px',
    }}>
      <SectionHeader>Upcoming</SectionHeader>
      {/* venue-gap dek (pique Phase 5, Zora spec §2) — the section's
          standfirst: one mono fog-blue line, typographic not chip, ZERO
          motion. Registry-run: psych('venue-gap') owns the string,
          threshold and gate; this component renders the result or NOTHING
          (degrade on this surface = absence — the list below IS the
          count-free state; no placeholder, no reserved space). Subject
          pins both counts to THIS list (buildVenueGapSubject over the
          upcoming slugs — reconciliation by construction: total ≡ the
          rows the anchor jumps to). Ships dark behind
          PSYCH_GATES['personalization-gate']. Region budget: this dek is
          the section-header region's single loaded element; the
          sale-status pills live in the row region — no contest (G7). */}
      {(() => {
        if (typeof window === 'undefined'
          || typeof window.psych !== 'function'
          || typeof window.buildVenueGapSubject !== 'function'
          || typeof window.readInterested !== 'function') return null;
        const slugs = upcoming.map((ev) => ev.slug).filter(Boolean);
        const subject = window.buildVenueGapSubject(
          slugs, window.readInterested(), '#venue-upcoming', ''
        );
        const gap = window.psych('venue-gap', subject, {
          surface: 'venue-page',
          dataBuiltAt: eventsGeneratedAt || undefined,
        });
        if (!gap) return null;
        return (
          <a
            href={gap.closeHref}
            className="focus-double-ring"
            aria-label={gap.ariaLabel}
            onClick={(e) => {
              // Skip-to-list semantics: move focus with the jump (Zora §4).
              e.preventDefault();
              const target = document.getElementById('venue-upcoming');
              if (target) { target.focus(); target.scrollIntoView({ block: 'start' }); }
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap',
              minHeight: 44, padding: '8px 0', margin: '-8px 0 20px',
              paddingLeft: 40, // header's 24px bar + 16px gap — aligns to headline text
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 12, fontWeight: 700, letterSpacing: 1,
              textTransform: 'uppercase', fontVariantNumeric: 'tabular-nums',
              // Fog data-voice pair (Zora §1 — hub-minutes precedent; both AA)
              color: darkMode ? '#A3BDCC' : '#3F5E70',
              textDecoration: 'none', cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
          >{gap.text}&nbsp;→</a>
        );
      })()}
      {upcoming.length === 0 ? (
        // Side-stripe replacement (Zora 2026-05-26): Device C brutalist
        // offset shadow on a 16px padding block. Replaces 5px amber
        // border-left with the project's signature CTA shadow language.
        <p className="brutalist-shadow" style={{
          fontFamily: "'Barlow', sans-serif",
          // AA-compliant fallback (was failing #888 at 3.5:1 on white).
          color: 'var(--on-surface-muted, #6B5B4F)',
          fontSize: 16,
          lineHeight: 1.55,
          padding: 16,
          background: 'var(--surface-container-low, transparent)',
          marginRight: 'var(--hard-shadow-offset, 4px)',
        }}>No upcoming shows on the calendar right now.</p>
      ) : (
        /* #venue-upcoming — the gap dek's close-href target: honest
           skip-to-list semantics (tabIndex -1 so focus can land). */
        <div id="venue-upcoming" tabIndex={-1} style={{ outline: 'none' }}>
          {hasThisWeek && (
            <>
              <ThisWeekBand count={thisWeek.length} darkMode={darkMode} />
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
                {thisWeek.map(ev => (
                  <UpcomingRow
                    key={ev.event_hash || ev.id}
                    ev={ev}
                    hot
                    isMobile={isMobile}
                    expanded={expandedId === (ev.event_hash || ev.id)}
                    onToggleExpand={() => toggleExpanded(ev.event_hash || ev.id)}
                    darkMode={darkMode}
                  />
                ))}
              </ul>
            </>
          )}
          {monthGroups.map((group, idx) => (
            <React.Fragment key={group.key}>
              <MonthHeader
                label={group.label}
                isFirst={idx === 0 && !hasThisWeek}
                isDesktop={isDesktop}
                darkMode={darkMode}
              />
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {group.events.map(ev => (
                  <UpcomingRow
                    key={ev.event_hash || ev.id}
                    ev={ev}
                    isMobile={isMobile}
                    expanded={expandedId === (ev.event_hash || ev.id)}
                    onToggleExpand={() => toggleExpanded(ev.event_hash || ev.id)}
                    darkMode={darkMode}
                  />
                ))}
              </ul>
            </React.Fragment>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <>
          <SectionHeader dim>Played here recently</SectionHeader>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, opacity: 0.9 }}>
            {past.map(ev => <PastRow key={ev.event_hash || ev.id} ev={ev} isMobile={isMobile} />)}
          </ul>
        </>
      )}
    </section>
  );
}
window.VenueEventList = VenueEventList;
